import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { TextField, Tooltip as MuiTooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFetch } from '../api';
import {
  RESOURCES_BY_TYPE, STATUS_BADGE, STATUS_LABEL,
} from '../sandbox/sandboxConfig';
import { getIndexedFields, DATE_OPERATORS, getStatusEnums } from './objectViewerConfig';
import ObjectResponsePanel from './ObjectResponsePanel';

// dashboard calls go through /internal so they stay out of the customer's api logs
const connectionHeader = (connectionId) => ({ 'x-trackstar-connection-id': connectionId });

const AUTOCOMPLETE_SX = {
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    height: '36px',
    fontSize: '0.6875rem',
    fontFamily: "'JetBrains Mono', monospace",
    backgroundColor: '#fff',
  },
};

const AUTOCOMPLETE_SLOT_PROPS = {
  paper: {
    sx: {
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      border: '1px solid #E7E5E4',
      mt: 0.5,
      fontSize: '0.6875rem',
      fontFamily: "'JetBrains Mono', monospace",
    },
  },
};

const RESULT_LIMIT = 100;

// let the connection picker match on customer id as well as connection id
const filterConnectionOptions = createFilterOptions({
  stringify: (option) => `${option.connection_id} ${option.customer_id || ''}`,
});

function getItemLabel(row) {
  return row.order_number || row.purchase_order_number
    || row.invoice_number || row.sku || row.name || null;
}

function ObjectViewerPage({ connectionData, openApiSpec }) {
  const trackstar = useFetch();
  const [searchParams, setSearchParams] = useSearchParams();

  // capture initial url params once before any effects clear them
  const initialParams = useRef({
    connection: searchParams.get('connection'),
    resource: searchParams.get('resource'),
    qmode: searchParams.get('qmode'),
    id: searchParams.get('id'),
    field: searchParams.get('field'),
    op: searchParams.get('op'),
    value: searchParams.get('value'),
    selected: searchParams.get('selected'),
  });
  // tracks restore lifecycle: 'pending' -> 'resource' -> 'done'
  // 'pending': waiting for connection restore
  // 'resource': connection restored, waiting for resource restore
  // 'done': fully restored, normal operation
  const restorePhase = useRef(
    initialParams.current.connection ? 'pending' : 'done',
  );
  const shouldAutoQuery = useRef(!!initialParams.current.connection
    && !!initialParams.current.resource);

  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [queryMode, setQueryMode] = useState(
    initialParams.current.qmode || 'id',
  );

  // id mode
  const [objectId, setObjectId] = useState(
    initialParams.current.id || '',
  );

  // filter mode
  const [filterField, setFilterField] = useState(
    initialParams.current.field || '',
  );
  const [filterOperator, setFilterOperator] = useState(
    initialParams.current.op || 'eq',
  );
  const [filterValue, setFilterValue] = useState(
    initialParams.current.value || '',
  );

  // results
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [hasQueried, setHasQueried] = useState(false);
  const [hitLimit, setHitLimit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // restore connection from url when connectionData arrives
  useEffect(() => {
    if (restorePhase.current !== 'pending') return;
    if (!connectionData || connectionData.length === 0) return;
    const urlConn = initialParams.current.connection;
    if (urlConn) {
      const match = connectionData.find(
        (c) => c.connection_id === urlConn,
      );
      if (match) {
        setSelectedConnection({ ...match, label: match.connection_id });
        return;
      }
    }
    restorePhase.current = 'done';
  }, [connectionData]);

  // clear results on connection change
  useEffect(() => {
    if (!selectedConnection) return;
    if (restorePhase.current === 'done') {
      setResults([]);
      setSelectedResult(null);
      setError(null);
      setHitLimit(false);
    }
  }, [selectedConnection]);

  // filter available resources by connection's integration_type
  const availableResources = selectedConnection
    ? (RESOURCES_BY_TYPE[selectedConnection.integration_type] || [])
    : [];

  // restore resource from url, or reset on connection change
  useEffect(() => {
    if (restorePhase.current === 'pending' && availableResources.length > 0) {
      const urlResource = initialParams.current.resource;
      if (urlResource) {
        const match = availableResources.find(
          (r) => r.key === urlResource,
        );
        if (match) {
          restorePhase.current = 'resource';
          setSelectedResource(match);
          return;
        }
      }
      restorePhase.current = 'done';
    }
    if (restorePhase.current === 'done') {
      setSelectedResource(null);
      setResults([]);
      setSelectedResult(null);
      setHitLimit(false);
    }
  }, [selectedConnection, availableResources.length]);

  // reset query inputs when resource changes (skip during restore)
  useEffect(() => {
    if (restorePhase.current !== 'done') return;
    setObjectId('');
    setFilterField('');
    setFilterOperator('eq');
    setFilterValue('');
    setResults([]);
    setSelectedResult(null);
    setError(null);
    setHitLimit(false);
    setHasQueried(false);
  }, [selectedResource]);

  // reset results when switching query mode
  useEffect(() => {
    if (restorePhase.current !== 'done') return;
    setError(null);
    setResults([]);
    setSelectedResult(null);
    setHitLimit(false);
    setHasQueried(false);
  }, [queryMode]);

  const indexedFields = selectedResource
    ? getIndexedFields(selectedResource.key) : [];
  const selectedFieldConfig = indexedFields.find(
    (f) => f.key === filterField,
  );
  const isDateField = selectedFieldConfig?.type === 'date';
  const isStatusField = filterField === 'status';
  const statusEnums = isStatusField
    ? getStatusEnums(openApiSpec, selectedResource?.apiPath) : null;

  // reset operator when field changes
  useEffect(() => {
    if (restorePhase.current === 'done') setFilterOperator(isDateField ? 'gte' : 'eq');
  }, [filterField]);

  // set default filter field when resource changes
  useEffect(() => {
    if (restorePhase.current === 'resource') {
      restorePhase.current = 'done';
      return;
    }
    if (restorePhase.current === 'done' && indexedFields.length > 0) {
      setFilterField(indexedFields[0].key);
    }
  }, [selectedResource]);

  // sync state to url params
  useEffect(() => {
    if (restorePhase.current !== 'done') return;
    const params = { mode: 'production' };
    if (selectedConnection) {
      params.connection = selectedConnection.connection_id;
    }
    if (selectedResource) params.resource = selectedResource.key;
    if (queryMode !== 'id') params.qmode = queryMode;
    if (queryMode === 'id' && objectId) params.id = objectId;
    if (queryMode === 'filter') {
      if (filterField) params.field = filterField;
      if (filterOperator !== 'eq') params.op = filterOperator;
      if (filterValue) params.value = filterValue;
    }
    if (selectedResult) params.selected = selectedResult.id;
    setSearchParams(params, { replace: true });
  }, [selectedConnection, selectedResource, queryMode,
    objectId, filterField, filterOperator, filterValue,
    selectedResult]);

  const handleListFetch = async (autoSelectId) => {
    if (!selectedConnection || !selectedResource) return;
    const { apiPath } = selectedResource;
    setLoading(true);
    setError(null);
    setResults([]);
    setSelectedResult(null);
    setHitLimit(false);
    try {
      const url = `internal/${apiPath}?limit=${RESULT_LIMIT}`;
      const data = await trackstar(url, 'GET', null, 'json', connectionHeader(selectedConnection.connection_id));
      if (data.error) {
        setError(data.error);
      } else {
        const list = data.data || [];
        setResults(list);
        setHitLimit(list.length >= RESULT_LIMIT);
        if (list.length === 1) {
          setSelectedResult(list[0]);
        } else if (autoSelectId) {
          const match = list.find((r) => r.id === autoSelectId);
          if (match) setSelectedResult(match);
        }
      }
      setHasQueried(true);
    } catch (err) {
      setError(err.message || 'Request failed');
    }
    setLoading(false);
  };

  const handleLookup = async (autoSelectId) => {
    if (!selectedConnection || !selectedResource) return;

    if (queryMode === 'id' && !objectId.trim()) {
      setError('Enter an object ID');
      return;
    }
    if (queryMode === 'filter' && !filterValue.trim()) {
      setError('Enter a filter value');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    setSelectedResult(null);
    setHitLimit(false);

    const { apiPath } = selectedResource;

    try {
      let url;
      if (queryMode === 'id') {
        url = `internal/${apiPath}/${objectId.trim()}`;
      } else {
        let val = filterValue.trim();
        if (isDateField && !val.includes('T')) {
          val = `${val}T00:00:00Z`;
        }
        const op = isDateField ? filterOperator : 'eq';
        url = `internal/${apiPath}`
          + `?${filterField}[${op}]=${encodeURIComponent(val)}`
          + `&limit=${RESULT_LIMIT}`;
      }

      const data = await trackstar(url, 'GET', null, 'json', connectionHeader(selectedConnection.connection_id));

      if (data.error) {
        setError(data.error);
        setLoading(false);
        return;
      }

      if (queryMode === 'id') {
        const obj = data.data || data;
        setResults([obj]);
        setSelectedResult(obj);
      } else {
        const list = data.data || [];
        setResults(list);
        setHitLimit(list.length >= RESULT_LIMIT);
        if (list.length === 1) {
          setSelectedResult(list[0]);
        } else if (autoSelectId) {
          const match = list.find((r) => r.id === autoSelectId);
          if (match) setSelectedResult(match);
        }
      }
      setHasQueried(true);
    } catch (err) {
      setError(err.message || 'Request failed');
    }
    setLoading(false);
  };

  // unified fetch trigger: fires whenever a resource becomes active.
  // handles both url-restore (runs the restored query or lists with
  // auto-select) and fresh resource clicks (list first 100). keyed on the
  // resource only, since a connection change resets the resource first
  useEffect(() => {
    if (!selectedResource || !selectedConnection) return;

    if (shouldAutoQuery.current) {
      shouldAutoQuery.current = false;
      const hasSpecificQuery = queryMode === 'id'
        ? !!objectId.trim()
        : !!filterValue.trim();
      if (hasSpecificQuery) {
        handleLookup(initialParams.current.selected);
      } else {
        handleListFetch(initialParams.current.selected);
      }
      return;
    }

    handleListFetch();
  }, [selectedResource]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLookup();
  };

  const connectionOptions = (connectionData || [])
    .filter((c) => c.integration_name !== 'sandbox')
    .map((c) => ({
      ...c,
      label: c.connection_id,
    }));

  const showResultsList = results.length > 1;

  return (
    <>
      {/* main card */}
      <div className="flex-1 min-h-0 card overflow-hidden flex" style={{ minHeight: '500px' }}>
        {/* left sidebar */}
        <div className="w-[300px] min-w-[300px] border-r border-surface-200 flex flex-col h-full bg-surface-0">
          {/* connection selector */}
          <div className="px-3 pt-3 pb-2">
            <div className="text-[10px] font-medium text-surface-400 uppercase tracking-wider mb-1.5">Connection</div>
            <Autocomplete
              options={connectionOptions}
              value={selectedConnection}
              onChange={(_, newValue) => setSelectedConnection(newValue)}
              getOptionLabel={(option) => option.label || ''}
              filterOptions={filterConnectionOptions}
              isOptionEqualToValue={(opt, val) => opt.connection_id === val.connection_id}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <li
                    key={key}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...rest}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-surface-700">{option.connection_id}</span>
                      {option.customer_id && (
                        <span className="truncate text-[10px] text-surface-400">
                          {option.customer_id}
                        </span>
                      )}
                    </div>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  placeholder="Search connections..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon sx={{
                        fontSize: 16, color: '#A8A29E', ml: 0.5, mr: -0.25,
                      }}
                      />
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-input': { height: '15px' } }}
                />
              )}
              size="small"
              fullWidth
              slotProps={AUTOCOMPLETE_SLOT_PROPS}
              sx={AUTOCOMPLETE_SX}
            />
            {selectedConnection?.customer_id && (
              <div className="mt-1.5 text-[10px] text-surface-400 font-mono truncate">
                <span className="uppercase tracking-wider text-surface-300">Customer ID </span>
                {selectedConnection.customer_id}
              </div>
            )}
          </div>

          {/* resource nav */}
          {selectedConnection && (
            <>
              <div className="border-t border-surface-100 mt-1" />
              <div className="overflow-y-auto py-2">
                <div className="text-[10px] font-medium text-surface-400 uppercase tracking-wider px-3 mb-1">Resources</div>
                {availableResources.map((r) => {
                  const isActive = selectedResource?.key === r.key;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setSelectedResource(r)}
                      className={`w-full flex items-center px-3 py-1.5 text-xs transition-colors ${
                        isActive
                          ? 'bg-surface-100 font-medium text-surface-900 border-l-2 border-accent-500 pl-2.5'
                          : 'text-surface-600 hover:bg-surface-50'
                      }`}
                    >
                      <span className="truncate">{r.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* query controls */}
          {selectedResource && selectedConnection && (
            <>
              <div className="border-t border-surface-100" />
              <div className="px-3 py-3 space-y-2">
                {/* row 1: mode toggle + field */}
                <div className="flex items-center gap-2">
                  <div className="grid grid-cols-2 gap-0.5 border border-surface-200 rounded-md p-0.5 bg-surface-50">
                    {[['id', 'ID'], ['filter', 'Filter']].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setQueryMode(key)}
                        className={`px-2 py-1 text-xs font-medium rounded transition-colors text-center ${
                          queryMode === key
                            ? 'bg-white text-surface-900 shadow-xs'
                            : 'text-surface-400 hover:text-surface-600'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {queryMode === 'filter' && (
                    <select
                      value={filterField}
                      onChange={(e) => setFilterField(e.target.value)}
                      className="flex-1 min-w-0 h-[30px] px-2 text-xs font-medium bg-white border border-surface-200 rounded-md text-surface-700"
                    >
                      {indexedFields.map((f) => (
                        <option key={f.key} value={f.key}>{f.label}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* row 2: operator + value (always visible) */}
                <div className="flex items-center gap-2">
                  {queryMode === 'filter' && (
                    <select
                      value={isDateField ? filterOperator : 'eq'}
                      onChange={(e) => setFilterOperator(e.target.value)}
                      disabled={!isDateField}
                      className="h-[30px] w-14 px-1 text-xs font-medium bg-white border border-surface-200 rounded-md text-surface-700 disabled:opacity-60"
                    >
                      {isDateField ? (
                        DATE_OPERATORS.map((op) => (
                          <option key={op.key} value={op.key}>{op.label}</option>
                        ))
                      ) : (
                        <option value="eq">=</option>
                      )}
                    </select>
                  )}
                  {queryMode === 'filter' && statusEnums ? (
                    <select
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                      className="flex-1 min-w-0 h-[30px] px-2 text-xs bg-white border border-surface-200 rounded-md text-surface-700"
                    >
                      <option value="">Select status...</option>
                      {statusEnums.map((s) => (
                        <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={queryMode === 'filter' && isDateField ? 'date' : 'text'}
                      value={queryMode === 'id' ? objectId : filterValue}
                      onChange={(e) => (queryMode === 'id' ? setObjectId(e.target.value) : setFilterValue(e.target.value))}
                      onKeyDown={handleKeyDown}
                      placeholder={queryMode === 'id' ? 'Paste object ID...' : 'Value...'}
                      className="flex-1 min-w-0 h-[30px] px-2 text-xs bg-white border border-surface-200 rounded-md text-surface-700 placeholder:text-surface-400 focus:outline-none focus:border-surface-400"
                    />
                  )}
                </div>

                {/* row 3: button */}
                <button
                  type="button"
                  onClick={handleLookup}
                  disabled={loading}
                  className="w-full h-[30px] rounded-md text-xs font-semibold bg-surface-900 text-surface-0 hover:bg-surface-800 transition-colors disabled:opacity-50"
                >
                  {loading ? <ClipLoader size={12} color="#fff" /> : 'Look up'}
                </button>
              </div>
            </>
          )}

          {error && (
            <div className="mx-3 mb-3 text-xs text-error-dark bg-error-light/30 border border-error-light rounded-md px-2 py-1.5">
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </div>
          )}
        </div>

        {/* results list (middle column) */}
        {showResultsList && (
          <div className="w-[260px] min-w-[260px] border-r border-surface-200 flex flex-col h-full bg-surface-0">
            <div className="px-3 pt-3 pb-2 flex items-center gap-2">
              <span className="text-[10px] font-medium text-surface-400 uppercase tracking-wider">
                Results
              </span>
              <span className="text-[10px] text-surface-400">
                {results.length}{hitLimit ? '+' : ''}
              </span>
              {hitLimit && (
                <span className="text-[10px] text-warning-dark bg-warning-light/40 px-1 py-0.5 rounded ml-auto flex items-center gap-1">
                  first {RESULT_LIMIT} shown
                  <MuiTooltip title="Use the Trackstar API to retrieve all results" arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 12, cursor: 'pointer' }} />
                  </MuiTooltip>
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {results.map((row) => {
                const label = getItemLabel(row);
                const isSelected = selectedResult?.id === row.id;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setSelectedResult(row)}
                    className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-left transition-colors ${
                      isSelected
                        ? 'bg-surface-100 border-l-2 border-accent-500 pl-2.5'
                        : 'hover:bg-surface-50'
                    }`}
                  >
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-xs font-medium text-surface-700 truncate">
                        {label || row.id}
                      </span>
                      {label && (
                        <span className="text-[11px] text-surface-400 font-mono truncate">
                          {row.id}
                        </span>
                      )}
                    </div>
                    {row.status && (
                      <span className={`badge status-badge ${STATUS_BADGE[row.status] || 'badge-neutral'} text-[10px] py-0 px-1 flex-shrink-0`}>
                        {STATUS_LABEL[row.status] || row.status}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* detail panel (right) */}
        <ObjectResponsePanel
          row={selectedResult}
          resourceKey={selectedResource?.key}
          apiPath={selectedResource?.apiPath}
          connectionId={selectedConnection?.connection_id}
          loading={loading && !selectedResult}
          noResults={hasQueried && results.length === 0 && !loading}
          hasResults={results.length > 1}
        />
      </div>
    </>
  );
}

export default ObjectViewerPage;
